import type { Id } from "@clarent/backend/convex/_generated/dataModel";
import * as Sentry from "@sentry/react";
import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Navigate, redirect, useParams, type ErrorComponentProps } from "@tanstack/react-router";
import { useEffect } from "react";
import { AppShell } from "./components/AppShell";
import { errorMessage } from "./lib/convexError";
import { AccountPage } from "./routes/AccountPage";
import { AlertsPage } from "./routes/AlertsPage";
import { BoardPage } from "./routes/BoardPage";
import { SettingsPage } from "./routes/SettingsPage";

function RouteError({ error, reset }: ErrorComponentProps) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);
  return (
    <div className="panel mx-auto mt-10 max-w-lg p-6">
      <h2 className="app-title">This page hit an error</h2>
      <p className="mt-2 text-sm text-muted">{errorMessage(error)}</p>
      <button className="btn-secondary mt-4" onClick={reset}>
        Try again
      </button>
    </div>
  );
}

const rootRoute = createRootRoute({
  component: AppShell,
  errorComponent: RouteError,
  notFoundComponent: () => (
    <div className="panel mx-auto mt-10 max-w-lg p-6 text-sm">
      <h2 className="app-title">Page not found</h2>
    </div>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  // Keep the query string: Clerk invitation links can point at / with __clerk_ticket (D36).
  beforeLoad: () => {
    throw redirect({ to: "/board", search: true });
  },
});

const boardRoute = createRoute({ getParentRoute: () => rootRoute, path: "/board", component: () => <BoardPage /> });
const calendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/calendar",
  component: lazyRouteComponent(() => import("./routes/CalendarPage"), "CalendarPage"),
});
// Decision D38: jobs open over the board. Alert emails and the archive list link to /jobs/<id>.
const newJobRoute = createRoute({ getParentRoute: () => rootRoute, path: "/jobs/new", component: () => <BoardPage overlay={{ kind: "new" }} /> });
const jobRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/jobs/$jobId",
  component: function JobDrawerRoute() {
    const { jobId } = useParams({ from: "/jobs/$jobId" });
    return <BoardPage overlay={{ kind: "drawer", jobId: jobId as Id<"jobs"> }} />;
  },
});
const editJobRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/jobs/$jobId/edit",
  component: function EditJobRoute() {
    const { jobId } = useParams({ from: "/jobs/$jobId/edit" });
    return <BoardPage overlay={{ kind: "edit", jobId: jobId as Id<"jobs"> }} />;
  },
});
const alertsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/alerts", component: AlertsPage });
const settingsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/settings", component: SettingsPage });
const accountRoute = createRoute({ getParentRoute: () => rootRoute, path: "/account", component: AccountPage });
// Signed out, AppShell renders the Google callback here instead of this route. Signed in, there's
// nothing to finish. Not a beforeLoad redirect: that would run before Clerk reads the callback URL.
const ssoCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sso-callback",
  component: () => <Navigate to="/board" replace />,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  boardRoute,
  calendarRoute,
  newJobRoute,
  jobRoute,
  editJobRoute,
  alertsRoute,
  settingsRoute,
  accountRoute,
  ssoCallbackRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
